import { useForm, UseFormResetField } from 'react-hook-form';
import { ZodEffects, ZodObject, ZodRawShape } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@casops/store/hooks';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApiBaseQueryError, ModelName } from '@casops/store/api';
import {
  AssetApprovalActionModel,
  useProcessApprovalMutation,
} from '@casops/store/apis/assetApprovalsApi';
import {
  canSubmitDefault,
  CanSubmitProps,
} from '@casops/components/layout/model-form/useModelForm';
import { ModelFormControlProps } from '@casops/components/layout/model-form/ModelForm';
import {
  ErrorResponse,
  SuccessNotificationContext,
  handleFieldErrors,
  ServerErrorFields,
} from '@dmc/ui';
import { useNavigate } from 'react-router-dom';

type FormModel = AssetApprovalActionModel;

const modelName = ModelName.PendingApproval;

interface UseApprovalFormProps {
  id: number;
  action: FormModel;
  schema: ZodEffects<ZodObject<ZodRawShape>> | ZodObject<ZodRawShape>;
  serverErrorFields: ServerErrorFields<FormModel>;
  canSubmitCallback?: (props: CanSubmitProps) => boolean;
}

interface UseApprovalFormReturn extends ModelFormControlProps<FormModel> {
  resetField: UseFormResetField<FormModel>;
}

export const useApprovalForm = ({
  id,
  action,
  schema,
  serverErrorFields,
  canSubmitCallback = canSubmitDefault,
}: UseApprovalFormProps): UseApprovalFormReturn => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting, isValid, dirtyFields },
    resetField,
    trigger,
  } = useForm<FormModel>({
    defaultValues: action,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  // Validate on mount to highlight any fields with errors.
  useEffect(() => {
    trigger();
  }, [trigger]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // Don't use the isDirty flag with ReactHookForm - see https://github.com/react-hook-form/react-hook-form/issues/3213.
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;

  // The canSubmitCallback allows the parent to determine if the form can be submitted based on model-specific logic.
  const canSubmit = canSubmitCallback({
    isSubmitting,
    isAnyFieldDirty,
    isValid,
    editMode: true, // This form only supports editing.
  });

  const [triggerEditItem] = useProcessApprovalMutation();

  const onSubmitInternal = useCallback(
    async (values: FormModel) => {
      try {
        // Clear any existing error.
        setErrorMessage(undefined);

        // Override existing action values sith a subset of values from the form.
        const payload: FormModel = { ...action, ...values };
        const { newAsset } = await triggerEditItem({ id, payload }).unwrap();

        // If this point is reached, the edit action was successful, so show success message and close the side panel.
        newAsset && typeof newAsset.id === 'number'
          ? showSuccessMessage(
              t('Asset was created', { assetName: newAsset.n }),
              () => navigate(`/assets/asset/${newAsset.id}/details`),
              t('Model.Collection.Title', { modelName: ModelName.AssetDetail }),
              t('Asset Approved'),
            )
          : showSuccessMessage(t(`Model.Edit.Success`, { modelName }));
        dispatch(closeSidePanel());
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse(e);
        const validationError = errorResponse.getValidationError();

        if (validationError) {
          handleFieldErrors<FormModel>({
            serverErrorFields,
            validationError,
            setError,
          });
        }

        setErrorMessage(t('Failed!') + ' ' + errorResponse.getTitle());
      }
    },
    [
      action,
      triggerEditItem,
      id,
      showSuccessMessage,
      t,
      dispatch,
      navigate,
      serverErrorFields,
      setError,
    ],
  );

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  return {
    canSubmit,
    onSubmit,
    errorMessage,
    control,
    resetField,
  };
};

export default useApprovalForm;
